import dayjs from 'dayjs'
import { ChangeEvent, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getNovelties } from '../../../../../redux/actions/novelties'
import { createSeminarLesson, toggleFormSeminarLesson } from '../../../../../redux/actions/seminarlessons'
import { formValidator, resetFormValidator } from '../../../../../redux/actions/validator'
import { IRootState } from '../../../../../redux/types'
import { ISeminarLesson } from '../../models/Seminars'

type FormElement = ChangeEvent<HTMLInputElement>

export const useSeminarLesson = () => {
  const dispatch = useDispatch()
  const { seminarSelected } = useSelector((state: IRootState) => state.seminar)
  const { seminarlesson_selected, showForm } = useSelector((state: IRootState) => state.seminarlesson)
  const { novelties } = useSelector((state: IRootState) => state.novelty)
  const { isValidForm, errorForm, errorTab, msgForm } = useSelector((state: IRootState) => state.validator)
  const { globalFamilyId, globalBrandId, globalLanguageId } = useSelector((state: IRootState) => state.global)

  const [seminarLessonState, setSeminarLessonState] = useState<ISeminarLesson>({
    id: null,
    novelties: [],
    fileNames: [],
    seminar_id: seminarSelected.id,
    title: '',
    content: '',
    extracontent: null,
    type: 0,
    order: null,
    duration: 1,
    datetime: null,
    notifications: { email: 0, notification: 0, integration: 0, hierarchy: 0 }
  })

  const [currentTab, setCurrentTab] = useState(0)

  useEffect(() => {
    if (seminarlesson_selected != null) {
      setSeminarLessonState({
        id: seminarlesson_selected.id,
        novelties: seminarlesson_selected.noveltiesIds,
        fileNames: [],
        seminar_id: seminarlesson_selected.seminar_id,
        title: seminarlesson_selected.title,
        content: seminarlesson_selected.content,
        extracontent: seminarlesson_selected.extracontent,
        type: seminarlesson_selected.type,
        order: seminarlesson_selected.order,
        duration: seminarlesson_selected.duration,
        datetime: seminarlesson_selected.datetime != null ? dayjs(seminarlesson_selected.datetime).format('YYYY-MM-DD HH:mm:ss') : null,
        notifications:
          seminarlesson_selected.notifications !== null
            ? JSON.parse(seminarlesson_selected.notifications)
            : { email: 0, notification: 0, integration: 0, hierarchy: 0 }
      })
    }
  }, [seminarlesson_selected])

  useEffect(() => {
    dispatch(
      getNovelties({
        family_id: globalFamilyId,
        brand_id: globalBrandId,
        language_id: globalLanguageId,
        requesttype: 0,
        type: 'novelties'
      })
    )
  }, [dispatch, globalFamilyId, globalBrandId, globalLanguageId])

  useEffect(() => {
    if (isValidForm) {
      dispatch(resetFormValidator())
      dispatch(createSeminarLesson(seminarLessonState))
    }
  }, [isValidForm, dispatch, seminarLessonState])

  const handleInputChange2 = (inputName: string, value: any) => {
    setSeminarLessonState(prev => ({ ...prev, [inputName]: value }))
  }

  const handleInputChange = (inputName: string) => (event: FormElement) => {
    const value = event.target.value
    setSeminarLessonState(prev => ({ ...prev, [inputName]: value }))
  }

  const handleNotificationsChange = (inputName: string) => (event: FormElement) => {
    let value: string | boolean
    if (inputName === 'hierarchy') {
      value = event.target.value
    } else {
      value = event.target.checked
    }

    setSeminarLessonState(prev => ({
      ...prev,
      notifications: { ...prev.notifications, [inputName]: value }
    }))
  }

  const handleMultiAutoCompleteChange = (inputName: string, value: any) => {
    let optionsMulti: number[] = []

    if (value !== null) {
      optionsMulti = value.map((item: any) => item.id)
    }

    setSeminarLessonState(prev => ({ ...prev, [inputName]: optionsMulti }))
  }

  const handleEditorChange = (e: any) => {
    setSeminarLessonState(prev => ({ ...prev, content: e.target.getContent() }))
  }

  const handleDateTimeChange = (newValue: dayjs.Dayjs | null) => {
    setSeminarLessonState(prev => ({
      ...prev,
      datetime: newValue ? newValue.format('YYYY-MM-DD HH:mm:ss') : null
    }))
  }

  const handleTypeChange = (value: any) => {
    setSeminarLessonState(prev => ({ ...prev, type: value.id }))
  }

  const handleContentChange = (value: string) => {
    setSeminarLessonState(prev => ({ ...prev, content: value }))
  }

  const handleExtraContentChange = (value: string) => {
    setSeminarLessonState(prev => ({ ...prev, extracontent: value }))
  }

  const getUploadParams = ({ meta }: any) => {
    setSeminarLessonState(prev => ({
      ...prev,
      fileNames: [...prev.fileNames, meta.name]
    }))
    return {
      url: `${import.meta.env.VITE_API_URL}/seminarlessons/uploaddropzone/${meta.name}`
    }
  }

  const handleSubmit = () => {
    dispatch(
      formValidator({
        title: [seminarLessonState.title, 'required|max:50|min:2', 'general'],
        seminar: [seminarLessonState.seminar_id, 'required|numeric', 'general'],
        content: [seminarLessonState.content, 'notempty', 'general'],
        order: [seminarLessonState.order, 'required|numeric|min:1', 'general'],
        duration: [seminarLessonState.duration, 'required|numeric|min:1', 'general']
      })
    )
  }

  const handleChangeTab = (_event: ChangeEvent<unknown>, newValue: number) => {
    setCurrentTab(newValue)
  }

  const handleCloseForm = () => {
    dispatch(toggleFormSeminarLesson())
  }

  return {
    seminarLessonState,
    currentTab,
    seminarSelected,
    novelties,
    showForm,
    errorForm,
    errorTab,
    msgForm,
    isValidForm,
    handleInputChange,
    handleInputChange2,
    handleNotificationsChange,
    handleMultiAutoCompleteChange,
    handleEditorChange,
    handleDateTimeChange,
    handleTypeChange,
    handleContentChange,
    handleExtraContentChange,
    getUploadParams,
    handleSubmit,
    handleChangeTab,
    handleCloseForm
  }
}
